import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDesign } from '../contexts/DesignContext';

const tileAccentBackgrounds = [
  'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(255,247,250,0.98) 100%)',
  'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(247,249,255,0.98) 100%)',
  'linear-gradient(135deg, rgba(255,255,255,0.96) 0%, rgba(250,247,255,0.98) 100%)'
];

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryImageErrors, setCategoryImageErrors] = useState({});
  const { designConfig } = useDesign();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      if (data.success) {
        setCategories(data.data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAssetUrl = (assetPath) => {
    if (!assetPath) return '';
    if (/^https?:\/\//i.test(assetPath)) return assetPath;

    const API_BASE = process.env.REACT_APP_API_URL || '';
    const normalizedPath = assetPath.startsWith('/') ? assetPath : `/${assetPath}`;
    return `${API_BASE}${normalizedPath}`;
  };

  const getCategoryIcon = (categoryName) => {
    const name = (categoryName || '').toLowerCase();

    if (name.includes('bag') || name.includes('handbag') || name.includes('purse')) {
      return (
        <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
      );
    }

    if (name.includes('dress') || name.includes('clothing') || name.includes('fashion')) {
      return (
        <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      );
    }

    if (name.includes('toy') || name.includes('accessory')) {
      return (
        <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      );
    }

    if (name.includes('jewelry') || name.includes('jewellery')) {
      return (
        <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
        </svg>
      );
    }

    return (
      <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    );
  };

  return (
    <div
      className="min-h-screen pb-24 md:pb-10"
      style={{ backgroundColor: designConfig.background_color || '#FFF8F5' }}
    >
      <section className="px-4 pt-5 pb-3 sm:px-6 sm:pt-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-[28px] px-5 py-5 sm:px-7 sm:py-6" style={{
            background: `linear-gradient(135deg, ${designConfig.primary_color || '#D4AF37'} 0%, ${designConfig.secondary_color || '#C9A9DD'} 52%, ${designConfig.accent_color || '#F4A5C7'} 100%)`
          }}>
            <div className="max-w-2xl text-white">
              <div className="text-[11px] font-semibold uppercase tracking-[0.32em] text-white/75">Collections</div>
              <h1 className="mt-2 text-3xl font-semibold leading-tight sm:text-4xl" style={{ fontFamily: 'serif' }}>
                Shop by Category
              </h1>
              <p className="mt-2 max-w-xl text-sm leading-6 text-white/90 sm:text-base">
                Browse faster with compact category tiles designed for mobile shopping.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 py-3 sm:px-6 sm:py-5">
        <div className="mx-auto max-w-6xl">
          {loading ? (
            <div className="grid grid-cols-3 gap-x-3 gap-y-5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6">
              {Array.from({ length: 12 }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="aspect-square rounded-[22px] bg-white/80 shadow-sm ring-1 ring-black/5" />
                  <div className="mx-auto mt-2 h-3 w-4/5 rounded bg-white/70" />
                </div>
              ))}
            </div>
          ) : categories.length === 0 ? (
            <div className="rounded-3xl bg-white/80 px-5 py-12 text-center shadow-sm ring-1 ring-black/5">
              <p className="text-sm sm:text-base" style={{ color: designConfig.text_color || '#111827' }}>
                No categories available at the moment.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-x-3 gap-y-5 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 lg:gap-x-5 lg:gap-y-8">
              {categories.map((category, index) => (
                <Link
                  key={category.id}
                  to={`/products?category=${category.id}`}
                  className="group"
                >
                  <div className="flex flex-col items-center text-center">
                    <div
                      className="relative aspect-square w-full overflow-hidden rounded-[22px] shadow-sm ring-1 ring-black/5 transition duration-200 group-active:scale-[0.98] sm:rounded-[24px]"
                      style={{ background: tileAccentBackgrounds[index % tileAccentBackgrounds.length] }}
                    >
                      <div className="absolute inset-0 rounded-[22px] bg-gradient-to-b from-white/10 via-transparent to-black/[0.02] sm:rounded-[24px]" />
                      <div className="flex h-full items-center justify-center p-2 sm:p-3">
                        {category.image && !categoryImageErrors[category.id] ? (
                          <img
                            src={getAssetUrl(category.image)}
                            alt={category.name}
                            className="h-full w-full rounded-[18px] object-contain"
                            loading="lazy"
                            onError={() => {
                              setCategoryImageErrors((prev) => ({
                                ...prev,
                                [category.id]: true
                              }));
                            }}
                          />
                        ) : (
                          <div
                            className="flex h-full w-full items-center justify-center rounded-[18px]"
                            style={{ color: designConfig.primary_color || '#D4AF37' }}
                          >
                            {getCategoryIcon(category.name)}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="mt-2 line-clamp-2 text-[13px] font-semibold leading-4 sm:text-sm sm:leading-5"
                      style={{ color: designConfig.text_color || '#111827' }}
                      title={category.name}
                    >
                      {category.name}
                    </div>

                    {!!category.product_count && (
                      <div className="mt-1 hidden text-[11px] text-gray-500 md:block">
                        {category.product_count} {category.product_count === 1 ? 'item' : 'items'}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Categories;

